import { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { Form } from "antd";

import { ConsortiumQuotationFormByTab } from "../../components/ConsortiumFormByTab";

import QuotationService, { DEFAULT_PAYMENT_PLAN_CODE } from "../../../../../../../../../shared/services/quotation";

import { GenericDataFormContext, QuotationInfoContext, StepOneFormContext } from "../../../../../shared/contexts";
import { StepThreeFormContext } from "./StepThreeFormContext";
import { StepTwoFormContext } from "../../../StepTwo/contexts/StepTwoForm";

import { ConsortiumQuotationComponent, ConsortiumQuotationRequest, DECISION_TYPE, GetCommertialsConditionsResponse, PropertyFeature, QuotationCoverage } from "../../../../../../../../../shared/interfaces";

import { generateInputID, getCoverageAndActivityKeyByID } from "../../../../../AP/Steps/StepThree/components/Quotation/utils";
import { getValueDefaultPaymentMethod } from "../../../../../utils/get-value-default-payment-method";
import { LOADING_SAVED_QUOTATIONS_STATES, QUOTATION_BUTTON, QuotationButton, QuotationStep } from "../../../../../shared";
import { WizardDataFormContext } from "../../../../Wizard/contexts/WizardDataForm/WizardDataFormContext";
import { LOADING_RENEWAL_QUOTATION_STATES } from "../../../../../shared/interfaces/loading-renewal-quotation-states-enum";
import { scrollToEmitButton } from "../../../../../../../../../shared/utils";

export interface ConsortiumQuotationForm {
  [key: string]: string;
}

export interface ConsortiumPaymentMethodForm {
  [key: string]:          string | number;
  paymentMethod:          string;
  comission:              number;
  extraVariable:          number;
  variation:              number;
};

export const StepThreeFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const [ consortiumPaymentMethodsForm ] = Form.useForm<ConsortiumPaymentMethodForm>();
  const [ stepThreeConsortiumQuotationForm ] = Form.useForm<ConsortiumQuotationForm>();

  const { 
    allLocalitiesPerProvince, 
    paymentMethodsList, 
    allCoveragesPerPlan, 
    setPlansSelectedByTabs, 
    plansSelected,
    setCanShowSpinner, 
    updateFormsAfterRemoveTabs, 
    setTabsUserCanNotDelete,
    removedTabIndexes, 
    setRemovedTabIndexes,
    formValues,
  } = useContext( GenericDataFormContext );
  const { clientSelected } = useContext( StepOneFormContext );
  const { handleTabErrors } = useContext(WizardDataFormContext);
  const { quotationInfo, loadSaveQuotationState, savedQuotation, setLoadSaveQuotationState, setCurrentStep, currentStep, renewalQuotationState, setRenewalQuotationState, quotationToRenewal, loadingSavedQuotation } = useContext( QuotationInfoContext );
  const { tabs, stepTwoForm, allFeatures } = useContext( StepTwoFormContext );

  const [ isLoadingForm, setIsLoadingForm ] = useState(false);
  const [ quotationTabs, setQuotationTabs ] = useState<Array<any>>([]);
  const [ consortiumQuotationComponents, setConsortiumQuotationComponents ] = useState<Array<ConsortiumQuotationComponent>>([]);
  const [ commertialConditions, setCommertialConditions ] = useState<GetCommertialsConditionsResponse>();
  const [ quotationResponse, setQuotationResponse ] = useState<any>();
  const [ labelButton, setLabelButton ] = useState<QuotationButton>( quotationResponse ? QUOTATION_BUTTON.RECOTIZAR : QUOTATION_BUTTON.COTIZAR );
  const [ canQuote, setCanQuote ] = useState<boolean>(true);
  const [ tabsThree, setTabsThree ] = useState<any[]>([]);
  const [ canCreateTabThree, setCanCreateTabThree ] = useState<boolean>(true);
  const [ activeTab, setActiveTab ] = useState(1);
  const [ canPostService, setCanPostService ] = useState<boolean>(false);

  const clearPrimaFieldsOnFormChange = useCallback(() => {
    const allFields = stepThreeConsortiumQuotationForm.getFieldsValue()
    const allPrimaKeys = Object.keys( allFields ).filter( el => el.includes('prima') );

    stepThreeConsortiumQuotationForm.resetFields( allPrimaKeys );
  }, [ stepThreeConsortiumQuotationForm ]);
    
  const getCommertialsConditions = useCallback(async () => {
    try {
      const response = await QuotationService.getComertialsConditions( quotationInfo?.branch! );
      
      if(response.data) {
        setCommertialConditions( response.data );
      }
    } catch (error) {}
  }, [quotationInfo, setCommertialConditions]);

  const createTabs = useCallback(() => {
    let newTabs;

    if (savedQuotation && canCreateTabThree) {
      newTabs = tabsThree?.map(({ key, label }: any) => ({
        children: <ConsortiumQuotationFormByTab tabKey={key} />,
        forceRender: true,
        closable: false,
        key,
        label,
      }));

      setCanCreateTabThree(false)
    } else {
      newTabs = tabs?.map(({ key, label }: any) => ({
        children: <ConsortiumQuotationFormByTab tabKey={key} />,
        forceRender: true,
        closable: false,
        key,
        label,
      }));
    }

    setQuotationTabs(newTabs);

    setPlansSelectedByTabs(savedQuotation && canCreateTabThree ? tabsThree : tabs, stepTwoForm);
  }, [tabsThree, setQuotationTabs, setPlansSelectedByTabs, stepTwoForm, tabs]);

  const generateCoveragesInfo = useCallback(( tabKey: string ): Array<QuotationCoverage> => {
    const quotationCoverages: Array<QuotationCoverage> = [];

    const allFields = stepThreeConsortiumQuotationForm.getFieldsValue();

    const allCheckboxFields = Object.keys(allFields).filter((el) => el.includes('checkbox'));

    const allCheckboxFieldByTab = allCheckboxFields.filter( el => el.includes(`consortium-tab-${tabKey}`));

    allCheckboxFieldByTab.forEach((el) => {
      if(stepThreeConsortiumQuotationForm.getFieldValue(el)) {
        const { id, activityKey } = getCoverageAndActivityKeyByID( el );

        const planSelected = stepThreeConsortiumQuotationForm.getFieldValue(`plan-tab-${tabKey}`);

        const allCoveragesByTab = allCoveragesPerPlan.find( el => String(el.plan as string) === String(planSelected) )?.coverages;

        const coverage = allCoveragesByTab?.find( el => el.id === id);

        const sumaAseguradaInputName = generateInputID( id, activityKey );

        const primaInputName = generateInputID( id, activityKey, 'prima' );

        quotationCoverages.push({
          cobertura: coverage?.codigo!,
          riesgo: coverage?.codigoRiesgo!,
          sumaAsegurada: stepThreeConsortiumQuotationForm.getFieldValue( sumaAseguradaInputName ),
          prima: stepThreeConsortiumQuotationForm.getFieldValue( primaInputName ) ?? '0',
          pormilaje: '0',
        });
      }
    });

    return quotationCoverages;
  }, [stepThreeConsortiumQuotationForm, allCoveragesPerPlan]);

  const generateFeaturesByTab = useCallback(
    (tabKey: number): Array<PropertyFeature> => {
      const features =
        allFeatures.find((el) => el.keyTab === tabKey)?.features ?? [];
      
      const featuresByTab: Array<PropertyFeature> = features.map((el) => ({
        aplica: stepTwoForm?.getFieldValue(
          `feature-tab-${tabKey}-${el.codigo}`
        ) ? DECISION_TYPE.YES : DECISION_TYPE.NO,
        codigo: el.codigo,
        codigoEquivalente: el.codigoEquivalente,
        descripcion: el.descripcion,
        incluyeWeb: DECISION_TYPE.YES,
        // tiene: fireFormValues.find((e: any) => e.name === `feature-tab-${tabKey}-${el.codigo}`).value
        tiene: stepTwoForm?.getFieldValue(
          `feature-tab-${tabKey}-${el.codigo}`
        )
          ? DECISION_TYPE.YES
          : DECISION_TYPE.NO,
      }));

      return featuresByTab;
    },
    [allFeatures, stepTwoForm]
  );

  const generateConsortiumComponentsInfo = useCallback((): Array<ConsortiumQuotationComponent> => {
    let components: Array<ConsortiumQuotationComponent> = [];

    tabs.forEach(({ key }: any) => {
      const location: string = stepTwoForm?.getFieldValue(`location-tab-${key}`);
      const provinceCode: string = stepTwoForm?.getFieldValue(`province-tab-${key}`); 
      
      const localitiesOfProvince = allLocalitiesPerProvince.find( el => el.provinceCode === provinceCode )?.localities;

      const riskZone = localitiesOfProvince?.find( el => `${el.sufijoCodigoPostal}${el.codigoPostal}` === location)?.zonaRiesgosVarios ?? '';

      const featuresByTab: Array<PropertyFeature> =
          generateFeaturesByTab(key);

      const component: ConsortiumQuotationComponent = {
        nroComponente: key,
        codPlan: stepTwoForm?.getFieldValue(`plan-tab-${key}`) as number,
        tipoVivienda: 0, //* es 0 para polizas nuevas.
        caracteristicasBien: featuresByTab,
        cantCaracteristicas: featuresByTab.length,
        sufijoCodigoPostal: location[0],
        codigoPostal: location.slice(1),
        zonaRiesgo: riskZone,
        buenResultado: 0, //* es 0 para polizas nuevas.
        cobertura: generateCoveragesInfo( key ),
        psua: 0, //* es 0 para polizas nuevas.
        capTarifa: '0',
        capTarifaInc1: '',
        capTarifaInc2: '',
        clasificacionRiesgo: 'A', 
      };

      components.push( component );
    });

    setConsortiumQuotationComponents( components );

    return components;
  }, [ tabs, allLocalitiesPerProvince, stepTwoForm, generateCoveragesInfo, setConsortiumQuotationComponents ]);

  const generateConsortiumQuotationForm = useCallback((): ConsortiumQuotationRequest => {
    const components = generateConsortiumComponentsInfo();
    const paymentMethodCode = consortiumPaymentMethodsForm.getFieldInstance('paymentMethod') ? consortiumPaymentMethodsForm?.getFieldValue('paymentMethod').split('-')[1] : getValueDefaultPaymentMethod(paymentMethodsList)
    const administrativeSurcharge = consortiumPaymentMethodsForm.getFieldInstance('extraVariable') ? consortiumPaymentMethodsForm?.getFieldValue('extraVariable') : commertialConditions?.porcentajeExtraPrimaVariable
    
    return {
      token: quotationInfo?.token!,
      arse: quotationInfo?.arse!,
      rama: quotationInfo?.branch!,
      codigoTipoFormaPago: paymentMethodCode,
      cantComponentes: components.length,
      cliente: clientSelected!,
      componentes: components,
      recargoAdministrativo: administrativeSurcharge,
    }
  }, [quotationInfo, clientSelected, generateConsortiumComponentsInfo, consortiumPaymentMethodsForm, commertialConditions]);

  const handlerOnClickEmit = useCallback(async () => {
    setCurrentStep(QuotationStep.FOUR);
  }, [setCurrentStep]);

  const updateFieldsBeforeQuotation = useCallback(
    ({ componentes }: any) => {
      const paymentMethodCode =
        consortiumPaymentMethodsForm.getFieldValue('paymentMethod');
      const defaultPaymentMethod = paymentMethodsList?.find(
        (el) => el.codigoFormaDePago === DEFAULT_PAYMENT_PLAN_CODE
      );

      if (
        paymentMethodCode &&
        paymentMethodsList?.find((el) => `${el.codigoFormaDePago}-${el.nroPlanDePago}` === paymentMethodCode)
      ) {
        consortiumPaymentMethodsForm.setFieldValue(
          'paymentMethod',
          paymentMethodCode
        );
      } else {
        consortiumPaymentMethodsForm.setFieldValue(
          'paymentMethod',
          `${defaultPaymentMethod?.codigoFormaDePago}-${defaultPaymentMethod?.nroPlanDePago}`
        );
      }

      if (
        !consortiumPaymentMethodsForm.getFieldValue('commission') &&
        !consortiumPaymentMethodsForm.getFieldValue('extraVariable')
      ) {
        consortiumPaymentMethodsForm.setFieldValue(
          'commission',
          Number(commertialConditions?.porcentajeDeComision)
        );
        consortiumPaymentMethodsForm.setFieldValue(
          'extraVariable',
          Number(commertialConditions?.porcentajeExtraPrimaVariable)
        );
      }

      componentes.forEach(({ nroDeComponente, coberturas }: any) => {
        coberturas.forEach(({ id, prima }: any) => {
          const primaInputName = generateInputID(
            id,
            `consortium-tab-${nroDeComponente}`,
            'prima'
          );

          stepThreeConsortiumQuotationForm.setFieldValue(
            primaInputName,
            Number(prima)
          );
        });
      });
    },
    [
      consortiumPaymentMethodsForm,
      paymentMethodsList,
      commertialConditions,
      stepThreeConsortiumQuotationForm,
      allCoveragesPerPlan,
    ]
  );

  const handlerOnClickQuotation = useCallback(
    async (form: ConsortiumQuotationRequest) => {
      setIsLoadingForm(true);

      try {
        await stepThreeConsortiumQuotationForm.validateFields();
        const response = await QuotationService.generateConsortiumQuotation(
          form
        );

        if (response.data) {
          scrollToEmitButton();
          setLabelButton(QUOTATION_BUTTON.EMITIR);

          const tabsUserCanNotDelete: Array<number> = [];

          response.data.cotizacionWebConsorcio.componentes.forEach(({ nroDeComponente, coberturas }) => {
            const planSelected = stepThreeConsortiumQuotationForm.getFieldValue(`plan-tab-${nroDeComponente}`);

            const coveragesPerPlan = allCoveragesPerPlan.find(el => (el.plan as string) === String(planSelected))?.coverages ?? [];

            coberturas?.forEach(coverage => {
              const originalCoverage = coveragesPerPlan.find( item => item.codigo  === coverage.codigoCobertura && item.codigoRiesgo === coverage.codigoRiesgo );
              coverage.id = originalCoverage?.id;
              coverage.subCategoriaDeTextos = originalCoverage?.subCategoriaDeTextos;
            });
            tabsUserCanNotDelete.push(Number(nroDeComponente));
          });

          setTabsUserCanNotDelete(tabsUserCanNotDelete);
          setQuotationResponse(response.data.cotizacionWebConsorcio);
          updateFieldsBeforeQuotation(response.data.cotizacionWebConsorcio);
          return;
        };
      } catch (error: any) {
        if (error && error?.errorFields?.length > 0) {
          handleTabErrors(error?.errorFields, ['tab'], 'Integral de Consorcio');
        }
      } finally {
        setIsLoadingForm(false);
      }
    },
    [
      stepThreeConsortiumQuotationForm,
      setLabelButton,
      setIsLoadingForm,
      setQuotationResponse,
      updateFieldsBeforeQuotation,
      setTabsUserCanNotDelete,
    ]
  );

  const handlerOnClick = useCallback(async () => {
    setCanShowSpinner(true);

    try {
      const form: ConsortiumQuotationRequest = generateConsortiumQuotationForm();
      
      if (labelButton === QUOTATION_BUTTON.EMITIR) {
        return await handlerOnClickEmit();
      }
      
      if (labelButton === QUOTATION_BUTTON.COTIZAR || labelButton === QUOTATION_BUTTON.RECOTIZAR)
        return await handlerOnClickQuotation(form);
    } catch {
    } finally{
      setCanShowSpinner(false);
    }
  }, [
    stepThreeConsortiumQuotationForm,
    labelButton,
    generateConsortiumQuotationForm,
    handlerOnClickEmit,
    handlerOnClickQuotation,
  ]);

  const fillStepThreeData = useCallback(async (isRenewal: boolean) => {
    
    const newTabs = tabs?.map(({ key }: any) => ({
      children: <ConsortiumQuotationFormByTab tabKey={key} />,
      forceRender: true,
      closable: false,
      key,
      label: `Integral de Consorcio ${key}`,
    }));

    let quotationToProccess;
    if(isRenewal){
      quotationToProccess = quotationToRenewal;
    }else{
      quotationToProccess = savedQuotation;
    }

    setTabsThree(newTabs);
    
    //* Se setea vació ya que no tenemos la primera respuesta de la COTIZACIÓN en un retome.
    setQuotationResponse({
      sumaAsegurada: '',
      premio: '',
      prima: '',
      componentes: quotationToProccess.bienes.map((good: any) => {        
        return {
          nroDeComponente: good.numeroComponente,
          producto: '',
          tipoVivienda: '',
          codigoPostal: '',
          sufijoCodigoPostal: '',
          zonaRiesgo: '',
          buenResultado: '',
          sumaAsegurada: '',
          premio: '',
          requiereInspeccion: '',
          porcentajeSumaAsegurada: '',
          capituloTarifa: '',
          capituloTarifaInc1: '',
          agravamiento: '',
          caracteristicas: good.caracteristicasSeleccionadas,
          coberturas: good.coberturasSeleccionadas,
          impuestos: [
            {
              codigoCobertura: '',
              impComision: '',
              impExtraPrimaFija: '',
              impImpuestosInternos: '',
              impIva: '',
              impIvaPercepcion: '',
              impIvaRnl: '',
              impIibbPercepcion: '',
              impIibbRiesgo: '',
              impIibbEmpresa: '',
              porImpuestosInternos: '',
              porIva: '',
              porIvaRnl: '',
              porIvaPercepcion: '',
              porTasaSsn: '',
              porServiciosSociales: '',
              impExtraPrimaVar: '',
              impRecargoFinanciero: '',
              selladoDeEmpresa: '',
              selladoDelRiesgo: '',
              impServiciosSociales: '',
              impTasaSsn: '',
              porComision: '',
              porExtraPrimaVar: '',
              porRecargoFinanciero: '',
              calculoImpuestos: '',
            },
          ],
          bonificaciones: [],
        };
      }),
      impuestos: {
        rama: '1',
        cantPolizaXRama: '',
        prima: '',
        porRecargoFinanciero: '',
        impRecargoFinanciero: '',
        impExtraPrimaFija: '',
        impSubtotalPrima: '',
        selladoDelRiesgo: '',
        selladoDeEmpresa: '',
        porImpuestosInternos: '',
        impImpuestosInternos: '',
        porServiciosSociales: '',
        impServiciosSociales: '',
        porTasaSsn: '',
        impTasaSsn: '',
        impIibbPercepcion: '',
        impIibbEmpresa: '',
        impIibbRiesgo: '',
        impIvaRnl: '',
        porIvaRnl: '',
        impIva: '',
        porIva: '',
      },
      condicionesComerciales: {
        cantPolizaXRama: '',
        porRecargoAdminist: '',
        impRecargoAdminist: '',
        porComisionProd: '',
        impComision: '',
        porRecargoAdminist1: '',
        porComisionProd1: '',
      },
      condicionesComerciales1: {
        rama: '1',
        porcRecargoAdminist: '',
      },
    });

    quotationToProccess.bienes.forEach((good: any, index: number) => {      
      const _tab = index + 1;

      good.coberturasSeleccionadas.forEach((coverage: any) => {
        stepThreeConsortiumQuotationForm.setFields([
          {
            name: `checkbox_${coverage.codigo}-${coverage.codigoRiesgo}_consortium-tab-${_tab}`,
            value: true,
          },
          {
            name: `prima_${coverage.codigo}-${coverage.codigoRiesgo}_consortium-tab-${_tab}`,
            value: coverage.prima,
          },
          {
            name: `sumaAsegurada_${coverage.codigo}-${coverage.codigoRiesgo}_consortium-tab-${_tab}`,
            value: coverage.sumaAsegurada,
          },
        ]);
      });

    });

    consortiumPaymentMethodsForm.setFields([
      { name: `paymentMethod`, value: `${quotationToProccess.formaDePago.codigo}-${quotationToProccess.formaDePago.codigoDePlanDePagos}` },
      {
        name: `commission`,
        value: quotationToProccess.formaDePago.porcentajeComision,
      },
      {
        name: `extraVariable`,
        value: quotationToProccess.formaDePago.porcentajeExtraPrimaVariable,
      },
      {
        name: `variation`,
        value:
          quotationToProccess.formaDePago.porcentajeComision -
          commertialConditions!.porcentajeDeComision,
      },
    ]);

    setLabelButton(QUOTATION_BUTTON.RECOTIZAR);
  }, [tabs, savedQuotation, setTabsThree, commertialConditions, quotationToRenewal]);

  const fillStepThreeServices = useCallback(async () => {
    await getCommertialsConditions();
  }, [getCommertialsConditions]);

  useEffect(() => {
    if (!plansSelected.length) createTabs();
  }, [createTabs]);

  useEffect(() => {
    getCommertialsConditions();
  }, [getCommertialsConditions]);

  useEffect(() => {
    if (removedTabIndexes.length && currentStep === QuotationStep.THREE && formValues) {
      updateFormsAfterRemoveTabs(removedTabIndexes, [stepThreeConsortiumQuotationForm], formValues);
      setRemovedTabIndexes([]);
    };
  }, [removedTabIndexes, currentStep, formValues]);

  useEffect(() => {
    const formValues = stepThreeConsortiumQuotationForm.getFieldsValue();
    
    if (Object.keys(formValues).length > 1) {
      setCanPostService(true);
    }
  }, [stepThreeConsortiumQuotationForm.getFieldsValue()]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING && canQuote && canPostService) {
      setCanQuote(false);

      handlerOnClick();
    }
  }, [
    loadSaveQuotationState,
    generateConsortiumQuotationForm,
    handlerOnClickQuotation
  ]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED) {
      fillStepThreeServices();
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING &&
      commertialConditions
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED);
    }
  }, [
    loadSaveQuotationState,
    commertialConditions,
  ]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED) {
      fillStepThreeData(false);
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING &&
      labelButton === QUOTATION_BUTTON.RECOTIZAR &&
      commertialConditions && tabsThree.length === tabs.length
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLED);
    }
  }, [
    loadSaveQuotationState,
    savedQuotation,
    tabs,
    labelButton,
    commertialConditions,
    tabsThree
  ]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING);
      setCurrentStep(QuotationStep.THREE);
    }
  }, [loadSaveQuotationState]);

  /**
   * Init Renewal
   */

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.FINISH_LOADING_RENEWAL && canQuote && canPostService) {
      
      setCanQuote(false);

      handlerOnClick();
    }
  }, [
    renewalQuotationState,
    generateConsortiumQuotationForm,
    handlerOnClickQuotation
  ]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL) {
      fillStepThreeServices();
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADING_RENEWAL);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADING_RENEWAL &&
      commertialConditions
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL);
    }
  }, [
    renewalQuotationState,
    commertialConditions,
  ]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL) {
      fillStepThreeData(true);
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLING_RENEWAL);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLING_RENEWAL &&
      labelButton === QUOTATION_BUTTON.RECOTIZAR &&
      commertialConditions && tabsThree.length === tabs.length
    ) {      
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLED_RENEWAL);
    }
  }, [
    renewalQuotationState,
    savedQuotation,
    tabs,
    labelButton,
    commertialConditions,
    tabsThree
  ]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.FINISH_LOADING_RENEWAL);
      setCurrentStep(QuotationStep.THREE);
    }
  }, [renewalQuotationState]);


  const values = useMemo(() => ({
    activeTab,
    canCreateTabThree,
    canQuote,
    clearPrimaFieldsOnFormChange,
    commertialConditions,
    consortiumPaymentMethodsForm,
    consortiumQuotationComponents,
    createTabs,
    generateConsortiumQuotationForm,
    handlerOnClick,
    isLoadingForm,
    labelButton,
    quotationResponse,
    quotationTabs,
    setActiveTab,
    setCanQuote,
    setIsLoadingForm,
    setLabelButton,
    setQuotationResponse,
    stepThreeConsortiumQuotationForm,
    tabsThree,
  }), [
    activeTab,
    canCreateTabThree,
    canQuote,
    clearPrimaFieldsOnFormChange,
    commertialConditions,
    consortiumPaymentMethodsForm,
    consortiumQuotationComponents,
    createTabs,
    generateConsortiumQuotationForm,
    handlerOnClick,
    isLoadingForm,
    labelButton,
    quotationResponse,
    quotationTabs,
    setActiveTab,
    setCanQuote,
    setIsLoadingForm,
    setLabelButton,
    setQuotationResponse,
    stepThreeConsortiumQuotationForm,
    tabsThree,
  ]);

  return (
    <StepThreeFormContext.Provider value={ values }>              
      { children }
    </StepThreeFormContext.Provider>
  )
}

